<template>
  <div>
    <div class="row date-navigator mt-3 mb-3">
      <div class="col-2">
        <button class="btn" :disabled="!canMovePrevMonth" @click="moveToPrevMonth">
          <i class="fas fa-chevron-left"></i>
        </button>
      </div>
      <div class="col-8">
        <h3 class="date-header">{{ monthName }}</h3>
      </div>
      <div class="col-2">
        <button class="btn" @click="moveToNextMonth">
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>
    <div class="row sort-and-filter mt-3 mb-3">
      <div class="col right">
        <div class="dropdown">
          <button
            class="btn dropdown-toggle"
            type="button"
            id="parkFilterButton"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >Park Filter</button>
          <div class="dropdown-menu" aria-labelledby="parkFilterButton">
            <button class="dropdown-item" type="button" @click="changeCalendarFilter(null)">All</button>
            <button
              class="dropdown-item"
              type="button"
              v-for="opCal in operatingCalendars"
              :key="opCal.operatingExtCalendarId"
              @click="changeCalendarFilter(opCal.operatingExtCalendarId)"
            >{{ opCal.displayName }}</button>
          </div>
        </div>
      </div>
    </div>
    <div class="desktop-only">
      <div class="cal-week-legend-row">
        <div class="cal-week-legend-cell">Sun</div>
        <div class="cal-week-legend-cell">Mon</div>
        <div class="cal-week-legend-cell">Tue</div>
        <div class="cal-week-legend-cell">Wed</div>
        <div class="cal-week-legend-cell">Thu</div>
        <div class="cal-week-legend-cell">Fri</div>
        <div class="cal-week-legend-cell">Sat</div>
      </div>
    </div>
    <div :class="{ 'loading': isLoading }">
      <CalendarDayPrevious v-for="dt in priorDays" :key="dt" :day="dt" />
      <CalendarDayCurrent
        v-for="operatingDay in currentDays"
        :key="operatingDay.calendarDate"
        :operatingDay="operatingDay"
      />
      <CalendarDayFuture v-for="dt in futureDays" :key="dt" :day="dt" />
      <div style="clear:left"></div>
    </div>
  </div>
</template>

<script lang="ts">
/* eslint-disable no-console */
import { defineComponent, onBeforeMount, inject, computed, toRefs, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { DateTime } from 'luxon';
import CalendarAppParam from "../composables/application-parameter";
import * as DateShared from "../composables/date-shared";
import * as CalendarModel from '../composables/calendar-model';
import * as ContentModel from '../composables/calendar-content-model';
import * as CalendarClient from "../composables/calendar-client";
import CalendarDayPrevious from './CalendarDayPrevious.vue';
import CalendarDayCurrent from './CalendarDayCurrent.vue';
import CalendarDayFuture from './CalendarDayFuture.vue';

export default defineComponent({
  components: { CalendarDayPrevious, CalendarDayCurrent, CalendarDayFuture },
  props: {
    year: { type: Number, required: true },
    month: { type: Number, required: true },
  },
  emits: [CalendarModel.Event.DataLoaded],
  setup(props, context) {
    // Static Properties
    const router = useRouter();
    const contentLookup = inject(ContentModel.ContentLookup)!;
    const appParam = inject(CalendarAppParam)!;
    const filteredExtCalendarId = inject(CalendarModel.FilteredExtCalendarId)!;
    const viewStartDate = DateTime.now().setZone(appParam.timeZoneName).startOf('month');

    // Reactive Properties
    const refProps = toRefs(props);
    const isLoading = ref(true);
    const monthStart = computed(() => 
      DateTime.utc(refProps.year.value, refProps.month.value, 1)
        .setZone(appParam.timeZoneName, {keepLocalTime: true})
    );
    const canMovePrevMonth = computed(() => 
      viewStartDate < monthStart.value
    );

    // Data setup
    const allCalendarDays = ref([] as CalendarModel.OperatingDaysResponseDto[] | null);
    const currentDays = ref([] as CalendarModel.OperatingDaysResponseDto[]);
    const setEmptyCalendar = () => {
      const emptyCurrentCalendar = [];
      for (let lxDt = monthStart.value; lxDt.month === monthStart.value.month; lxDt = lxDt.plus({days: 1})) {
        emptyCurrentCalendar.push({
          calendarDate: lxDt.toISODate(),
          operatingSchedules: [],
          featuredSchedules: []
        });
      }
      currentDays.value = emptyCurrentCalendar as CalendarModel.OperatingDaysResponseDto[];
    };
    setEmptyCalendar();

    const monthName = computed(() => {
      return monthStart.value.monthLong;
    });

    // Events
    const moveToPrevMonth = () => {
      const moveToDate = monthStart.value.minus({months: 1});
      router.replace(`/${moveToDate.year}/${moveToDate.month}`);
    };
    const moveToNextMonth = () => {
      const moveToDate = monthStart.value.plus({months: 1});
      router.replace(`/${moveToDate.year}/${moveToDate.month}`);
    };
    const changeCalendarFilter = (extCalendarId: string | null) => {
      filteredExtCalendarId.value = extCalendarId;
    };

    // Queries
    const getMonthData = async () => {
      setEmptyCalendar();
      isLoading.value = true;
      const queryEndDate = monthStart.value.endOf('month');

      allCalendarDays.value = await CalendarClient.getOperatingDays(
        monthStart.value.toISODate()!,
        queryEndDate.toISODate()!,
        contentLookup.value.operatingExtCalendarIds,
        contentLookup.value.monthlyFeaturedExtCalendarIds);

      if (!allCalendarDays.value || !allCalendarDays.value.length) {
        console.error("Unable to get calendar dates");
      }

      applyCalendarFilter();
      isLoading.value = false;
      context.emit(CalendarModel.Event.DataLoaded, false);
    };
    const applyCalendarFilter = () => {
      if(allCalendarDays.value === null) {
        setEmptyCalendar();
      } else if(filteredExtCalendarId.value === null) {
        currentDays.value = allCalendarDays.value;
      } else {
        currentDays.value = allCalendarDays.value.map(day => ({
          calendarDate: day.calendarDate,
          operatingSchedules: day.operatingSchedules.filter(cal=> cal.extCalendarId === filteredExtCalendarId.value),
          featuredSchedules: day.featuredSchedules.filter(cal=> cal.operatingExtCalendarId === filteredExtCalendarId.value)
        }));
      }
    };

    // Reactions
    onBeforeMount(getMonthData);
    watch(monthStart, getMonthData);
    watch(filteredExtCalendarId, applyCalendarFilter);

    // return Model
    return {
      monthName,
      priorDays: computed(() => DateShared.calendarWeekPrior(monthStart.value)),
      currentDays,
      futureDays: computed(() => DateShared.calendarWeekFollowing(monthStart.value)),
      canMovePrevMonth,
      moveToPrevMonth,
      moveToNextMonth,
      isLoading,
      operatingCalendars: computed(() => contentLookup.value.operatingCalendars),
      changeCalendarFilter
    };
  }
});

</script>
